import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

export const downloadAdmitCard = async (url, token) => { // Removed studentPhotoUrl parameter
  try {
    // Fetch API Data
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) throw new Error("Failed to fetch admit card data");

    const data = await response.json();
    if (!data.success || !data.data) throw new Error("Invalid admit card data structure");

    // Extract Student Data
    const studentData = data.data || {};
    // console.log(studentData.student_photo_url, "Student Photo URL"); // Log student photo URL

    const applicationNo = studentData.application_no || "N/A";
    const fullName = `${studentData.first_name || ""} ${studentData.last_name || ""}`.trim();
    const parentName = studentData.father_name || studentData.mother_name || "N/A";
    const className = studentData.class?.name || "N/A";
    const satsNumber = studentData.candidate_sats_no || "N/A";
    const dob = studentData.dob || "N/A";
    const address = `${studentData.address1 || ""}, ${studentData.city || ""}`.trim();

    // Create a PDF Document
    const pdfDoc = await PDFDocument.create();
    // A4 size is roughly 595 x 842 points
    const page = pdfDoc.addPage([595, 842]);

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const black = rgb(0, 0, 0);

    // Header Section
    // Load and Embed School Logo (Public Folder)
    let logoImage;
    try {
      const logoUrl = "/ssvk-logo.png"; // Fixed path based on instruction
      const logoImageBytes = await fetch(logoUrl).then(res => res.arrayBuffer());
      logoImage = await pdfDoc.embedPng(logoImageBytes);

      const logoHeight = 80;
      const logoWidth = logoHeight * (logoImage.width / logoImage.height);
      page.drawImage(logoImage, { x: 50, y: 720, width: logoWidth, height: logoHeight });
    } catch (e) {
      console.warn("Logo failed to load", e);
    }

    // Header Text
    page.drawText("Aum Shri Sathya Sai Nathaya Namaha", { x: 220, y: 790, size: 9, font: boldFont, color: black });
    page.drawText("Bhagavan Shri Sathya Sai Seva Trust®", { x: 190, y: 765, size: 14, font: boldFont, color: black });
    page.drawText("Sathya Sai Sevagiri, Post Chokkady, SulliaTq., D. K. Pin: 574212", { x: 140, y: 740, size: 12, font, color: black });

    // Main Title
    page.drawText("Sathya Sai Hostels – Admit Card", { x: 190, y: 700, size: 16, font: boldFont, color: black });
    // Underline the title
    page.drawLine({
      start: { x: 190, y: 695 },
      end: { x: 440, y: 695 },
      thickness: 1.5,
      color: black,
    });

    // Content Section Setup
    const startX = 50;
    const valueStartX = 240;
    const lineStartX = 250;
    const lineEndX = 420;
    let startY = 650;
    const lineSpacing = 30;

    // Content Data
    const fields = [
      { label: "Application No.", value: applicationNo },
      { label: "Name of the Candidate", value: fullName },
      { label: "Name of the Father", value: studentData.father_name || "N/A" },
      { label: "Name of the Mother", value: studentData.mother_name || "N/A" },
      { label: "Class for which admission is sought", value: className },
      { label: "SATS number", value: satsNumber },
      { label: "Date of the interview: 11 / 12-04-2026", value: "" }, // Merged label text per image spacing
      { label: "Time", value: "9 00AM to 12 00PM", isBoldValue: true },
      { label: "Venue", value: "Bhagawan Shri Sathya Sai Vidya Kendra (E.M), Chokkady" },
    ];

    fields.forEach((field, index) => {
      // Draw Label
      page.drawText(field.label, { x: startX, y: startY, size: 11, font: boldFont, color: black });

      if (index === 6) {
        // Special handling for the merged date row where standard gap doesn't apply
        page.drawText(":", { x: valueStartX, y: startY - 20, size: 11, font: boldFont, color: black });
        page.drawLine({ start: { x: lineStartX, y: startY - 20 - 2 }, end: { x: 330, y: startY - 20 - 2 }, thickness: 1, color: black });
        startY -= lineSpacing;
        return;
      }

      // Draw colon
      if (index !== 6) {
        page.drawText(":", { x: valueStartX, y: startY, size: 11, font: boldFont, color: black });
      }

      // Draw Value or Line based on field
      if (index < 6) {
        // Draw blank line and text above it
        page.drawLine({
          start: { x: lineStartX, y: startY - 2 },
          end: { x: lineEndX, y: startY - 2 },
          thickness: 1,
          color: black,
        });
        if (field.value) {
          page.drawText(field.value.toString(), { x: lineStartX + 5, y: startY + 2, size: 11, font, color: black });
        }
      } else {
        // Normal text without underline (Time & Venue)
        if (field.value) {
          const valFont = field.isBoldValue ? boldFont : font;
          page.drawText(field.value.toString(), { x: lineStartX + 5, y: startY, size: 11, font: valFont, color: black });

          if (field.isBoldValue) {
            // Underline the time
            page.drawLine({ start: { x: lineStartX + 5, y: startY - 2 }, end: { x: lineStartX + 120, y: startY - 2 }, thickness: 1, color: black });
          }
        }
      }

      startY -= lineSpacing;
    });

    // Right Side Photo Box
    const photoBoxX = 450;
    const photoBoxY = 530;
    const photoBoxWidth = 110;
    const photoBoxHeight = 130;

    // Draw Box
    page.drawRectangle({
      x: photoBoxX,
      y: photoBoxY,
      width: photoBoxWidth,
      height: photoBoxHeight,
      borderColor: black,
      borderWidth: 1,
    });

    // Try embedding the photo over the box if available
    let photoEmbedded = false;
    if (studentData.student_photo_url) {
      try {
        const photoResponse = await fetch(studentData.student_photo_url);
        if (photoResponse.ok) {
          const photoBytes = await photoResponse.arrayBuffer();
          const contentType = photoResponse.headers.get("content-type");
          let studentPhoto;
          if (contentType === "image/png") {
            studentPhoto = await pdfDoc.embedPng(photoBytes);
          } else if (contentType === "image/jpeg" || contentType === "image/jpg") {
            studentPhoto = await pdfDoc.embedJpg(photoBytes);
          }

          if (studentPhoto) {
            page.drawImage(studentPhoto, {
              x: photoBoxX,
              y: photoBoxY,
              width: photoBoxWidth,
              height: photoBoxHeight
            });
            photoEmbedded = true;
          }
        }
      } catch (error) {
        console.error("Failed to embed student photo:", error);
      }
    }

    if (!photoEmbedded) {
      // Box placeholder text (only if photo fails or doesnt exist)
      page.drawText("Affix a passport size", { x: photoBoxX + 10, y: photoBoxY + 80, size: 9, font, color: black });
      page.drawText("photo of the", { x: photoBoxX + 10, y: photoBoxY + 65, size: 9, font, color: black });
      page.drawText("candidate", { x: photoBoxX + 10, y: photoBoxY + 50, size: 9, font, color: black });
    }

    // Signatures Section
    const signatureY = 380;

    // Left Signature
    page.drawText("Signature of the Invigilator", { x: 50, y: signatureY, size: 10, font: boldFont, color: black });
    page.drawText("(B S S S V K Chokkady)", { x: 65, y: signatureY - 15, size: 10, font: boldFont, color: black });

    // Right Signature
    page.drawText("Signature of the Headmaster", { x: 400, y: signatureY, size: 10, font: boldFont, color: black });
    page.drawText("(B S S S V K Chokkady)", { x: 420, y: signatureY - 15, size: 10, font: boldFont, color: black });

    // Separator line
    page.drawLine({ start: { x: 0, y: 345 }, end: { x: 595, y: 345 }, thickness: 1, color: black });

    // Instructions block
    page.drawText("Please Note:", { x: 50, y: 310, size: 12, font: boldFont, color: black });
    page.drawText("1. Carry this admit card to attend the interview.", { x: 130, y: 310, size: 12, font: boldFont, color: black });
    page.drawText("2. All the candidates should be in the campus before 9.00am.", { x: 110, y: 280, size: 12, font: boldFont, color: black });

    // Second Separator line
    page.drawLine({ start: { x: 0, y: 250 }, end: { x: 595, y: 250 }, thickness: 1, color: black });

    // Lower Footer QR section
    page.drawText("Location of the", { x: 50, y: 200, size: 18, font: boldFont, color: black });
    page.drawLine({ start: { x: 50, y: 195 }, end: { x: 195, y: 195 }, thickness: 1.5, color: black });

    page.drawText("School:", { x: 400, y: 200, size: 18, font: boldFont, color: black });
    page.drawLine({ start: { x: 400, y: 195 }, end: { x: 465, y: 195 }, thickness: 1.5, color: black });

    // Embed specific QR code
    try {
      const qrCodeUrl = "/school-location-qr.png"; // Changed to exactly match your instruction
      const qrCodeImageBytes = await fetch(qrCodeUrl).then(res => res.arrayBuffer());
      const qrCodeImage = await pdfDoc.embedPng(qrCodeImageBytes);
      page.drawImage(qrCodeImage, { x: 230, y: 50, width: 140, height: 140 });
    } catch (error) {
      console.error("Failed to embed QR code:", error);
    }

    // Generate & Download the PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });

    if (!blob.size) {
      throw new Error("Failed to generate PDF file");
    }

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `AdmitCard_${applicationNo}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error downloading admit card:", error.message);
    alert("Failed to download admit card. Please try again.");
  }
};